import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTimer } from '../hooks/useTimer';
import Sidebar from './Sidebar/Sidebar';
import TopNav from './TopNav/TopNav';
import PatientHeader from './PatientHeader/PatientHeader';
import ChatContainer from './Chat/ChatContainer';
import ChatInput from './Chat/ChatInput';
import EvaluationModal from './Modals/EvaluationModal';
import ImageModal from './Modals/ImageModal';
import TimerModal from './Modals/TimerModal';
import LabReportsPanel from './LabReports/LabReportsPanel';
import AnketaPanel from './Anketa/AnketaPanel';
import { apiService, getPatientImageUrl } from '../services/api';
import { getCaseProfile, getLocalizedCaseDisplay } from '../data/caseProfiles';
import { useLanguage } from '../context/LanguageContext';
import { transcriptBackupKey } from '../utils/authSession';
import './EncounterWorkspace.css';

const encounterCopy = {
    en: { startError: 'The session could not be started. Please try again.', starting: 'Starting session…', waitForResponse: 'Wait for the current response before submitting your assessment.', casesError: 'Cases could not be loaded. Please try again.', retry: 'Try again', chatLimit: 'You have reached your free chat limit for today. Please contact your instructor to continue.' },
    ru: { startError: 'Не удалось начать сеанс. Повторите попытку.', starting: 'Начало сеанса…', waitForResponse: 'Дождитесь текущего ответа перед отправкой работы.', casesError: 'Не удалось загрузить случаи. Повторите попытку.', retry: 'Повторить попытку', chatLimit: 'Вы достигли дневного лимита бесплатных сообщений. Обратитесь к преподавателю, чтобы продолжить.' },
    uz: { startError: 'Seansni boshlab bo‘lmadi. Qayta urinib ko‘ring.', starting: 'Seans boshlanmoqda…', waitForResponse: 'Baholashni yuborishdan oldin joriy javobni kuting.', casesError: 'Holatlarni yuklab bo‘lmadi. Qayta urinib ko‘ring.', retry: 'Qayta urinish', chatLimit: 'Bugungi bepul xabarlar limitiga yetdingiz. Davom etish uchun o‘qituvchingizga murojaat qiling.' }
};

const DEFAULT_PHASES = [
    { name: "Anamnesis", minutes: 6 },
    { name: "Physical Exam", minutes: 5 },
    { name: "Differential & Tests", minutes: 5 },
    { name: "Evaluation & Management", minutes: 10 }
];

const compilePhases = (phases) => {
    let cumulative = 0;
    return phases.map((phase) => {
        const duration = Number(phase.minutes) * 60 * 1000;
        cumulative += duration;
        return { ...phase, duration, cumulative };
    });
};

const DEFAULT_COMPILED_PHASES = compilePhases(DEFAULT_PHASES);
const ASSESSMENT_PHASE = 'Evaluation & Management';
const getTotalDuration = (phases) => phases.reduce((total, phase) => total + phase.duration, 0);
const isMobileViewport = () => typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches;

const loadTranscriptBackups = (userId) => {
    if (!userId) return {};
    try {
        const backups = JSON.parse(localStorage.getItem(transcriptBackupKey(userId)) || '{}');
        return backups && typeof backups === 'object' && !Array.isArray(backups) ? backups : {};
    } catch {
        return {};
    }
};

const saveTranscriptBackup = (userId, caseId, submission) => {
    if (!userId || !caseId) return;
    const backups = loadTranscriptBackups(userId);
    backups[caseId] = submission;
    try {
        localStorage.setItem(transcriptBackupKey(userId), JSON.stringify(backups));
    } catch { /* A full browser cache must not turn a saved evaluation into a failure. */ }
};

const buildTranscript = (messages) => messages
    .filter(message => typeof message.text === 'string' && message.text.trim()
        && ['user', 'patient', 'system'].includes(message.transcriptRole))
    .map(message => ({
        role: message.transcriptRole,
        content: message.text,
        ...(message.investigationId ? { investigation_id: message.investigationId } : {})
    }));

const buildInitialChart = (caseItem, title) => {
    const profile = getCaseProfile(caseItem);
    const display = getLocalizedCaseDisplay(caseItem);
    return {
        title,
        caseId: display.id,
        patient: display.name,
        age: display.age,
        chiefComplaint: display.chiefComplaint,
        ...profile.initialChart,
        triage: display.triage,
        demographics: { ...profile.demographics, sex: display.gender, setting: display.setting }
    };
};

const localizeCaseSnapshot = (snapshot, language) => {
    if (!snapshot) return null;
    const translated = snapshot.localized_profiles?.[language];
    const profile = translated?.id === snapshot.id ? translated : snapshot;
    return { ...profile, chiefComplaint: profile.chief_complaint ?? profile.chiefComplaint ?? '' };
};

function Dashboard() {
    const { user } = useAuth();
    const { language, t, phaseName } = useLanguage();
    const copy = encounterCopy[language] || encounterCopy.en;
    const navigate = useNavigate();
    const [cases, setCases] = useState([]);
    const [casesLoading, setCasesLoading] = useState(true);
    const [selectedCase, setSelectedCase] = useState(null);
    const [tempSelectedCase, setTempSelectedCase] = useState(null);
    const [messages, setMessages] = useState([]);
    const acceptedTranscript = useMemo(() => buildTranscript(messages), [messages]);
    const [inputText, setInputText] = useState('');
    const [isTyping, setIsTyping] = useState(false);
    const [sidebarOpen, setSidebarOpen] = useState(() => !isMobileViewport());
    const [showEvaluationForm, setShowEvaluationForm] = useState(false);
    const [showImageModal, setShowImageModal] = useState(false);
    const [showTimerModal, setShowTimerModal] = useState(false);
    const [diagnosis, setDiagnosis] = useState('');
    const [managementPlan, setManagementPlan] = useState('');
    const [evaluationResult, setEvaluationResult] = useState(null);
    const [isEvaluating, setIsEvaluating] = useState(false);
    const [patientImage, setPatientImage] = useState('');
    const [imageZoom, setImageZoom] = useState(1);
    const [isTimerExpired, setIsTimerExpired] = useState(false);
    const [sessionActive, setSessionActive] = useState(false);
    const [sessionId, setSessionId] = useState(null);
    const [showLabReports, setShowLabReports] = useState(false);
    const [showAnketaPanel, setShowAnketaPanel] = useState(false);
    const [isStarting, setIsStarting] = useState(false);
    const [encounterError, setEncounterError] = useState('');
    const [casesError, setCasesError] = useState(false);
    const [caseRefresh, setCaseRefresh] = useState(0);
    const encounterRef = useRef({ controller: null, sessionId: null, pending: null });
    const nextMessageId = useRef(0);
    const invalidateEncounter = useCallback(() => {
        encounterRef.current.controller?.abort();
        encounterRef.current = { controller: null, sessionId: null, pending: null };
    }, []);
    const enterAssessment = useCallback(() => {
        setShowEvaluationForm(true);
        setShowImageModal(false);
        setShowLabReports(false);
        setShowAnketaPanel(false);
    }, []);

    useEffect(() => () => invalidateEncounter(), [invalidateEncounter]);

    useEffect(() => {
        if (user && user.role === 'doctor') {
            navigate('/doctor-review', { replace: true });
        }
    }, [user, navigate]);

    useEffect(() => {
        const handleResize = () => {
            setSidebarOpen(prev => (isMobileViewport() ? false : (prev || Boolean(selectedCase || sessionActive))));
        };
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [selectedCase, sessionActive]);

    const [completedSubmissions, setCompletedSubmissions] = useState({});
    const [showCompletedModal, setShowCompletedModal] = useState(false);
    const [completedModalCase, setCompletedModalCase] = useState(null);
    const [viewingTranscript, setViewingTranscript] = useState(null);

    const inactivityMessagesByLanguage = {
        en: [
            "Are you still there?",
            "Is everything alright?",
            "Should I wait for your response?",
            "Hello? Are you listening to me?",
            "Do you need more time to think?"
        ],
        ru: [
            "Вы ещё здесь?",
            "Всё в порядке?",
            "Мне подождать вашего ответа?",
            "Здравствуйте, вы меня слышите?",
            "Вам нужно больше времени подумать?"
        ],
        uz: [
            "Hali shu yerdamisiz?",
            "Hammasi joyidami?",
            "Javobingizni kutaymi?",
            "Assalomu alaykum, meni eshityapsizmi?",
            "O'ylash uchun ko'proq vaqt kerakmi?"
        ]
    };
    const inactivityMessages = inactivityMessagesByLanguage[language] || inactivityMessagesByLanguage.en;

    const [timerPhases, setTimerPhases] = useState(DEFAULT_COMPILED_PHASES);
    const [sessionDuration, setSessionDuration] = useState(getTotalDuration(DEFAULT_COMPILED_PHASES));
    const timer = useTimer(handleTimerExpired, 5 * 60 * 1000);
    const [currentPhase, setCurrentPhase] = useState(timerPhases[0].name);
    const [phaseTimeRemaining, setPhaseTimeRemaining] = useState(timerPhases[0].duration);
    // Derive the boundary from the schedule, including a background tab that
    // wakes after several stages. Opening the form never restarts the clock.
    const isAssessmentPhase = sessionActive && (isTimerExpired || currentPhase === ASSESSMENT_PHASE
        || timer.timeLeft <= timerPhases[timerPhases.length - 1].duration);
    useEffect(() => {
        if (isAssessmentPhase && !evaluationResult) enterAssessment();
    }, [isAssessmentPhase, evaluationResult, enterAssessment]);
    const formatPhaseNotice = (key, phase) => t(key)
        .replace('{phase}', phaseName(phase.name))
        .replace('{minutes}', String(Math.round(phase.duration / 60000)))
        .replace('{unit}', t('minutesShort'));

    useEffect(() => {
        if (!timer.isActive) return;

        const elapsedTime = sessionDuration - timer.timeLeft;
        const phase = timerPhases.find(p => elapsedTime < p.cumulative) || timerPhases[timerPhases.length - 1];

        // Calculate phase-specific time remaining
        const phaseStartTime = phase.cumulative - phase.duration;
        const phaseElapsed = elapsedTime - phaseStartTime;
        const phaseRemaining = Math.max(0, phase.duration - phaseElapsed);
        setPhaseTimeRemaining(phaseRemaining);

        if (phase.name !== currentPhase) {
            setCurrentPhase(phase.name);
            if (phase.name === ASSESSMENT_PHASE) return;

            // Notify user of phase change
            const phaseMsg = {
                id: ++nextMessageId.current,
                text: `🔔 ${formatPhaseNotice('phaseChangeStarting', phase)}`,
                sender: 'system',
                timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
            };
            setMessages(prev => [...prev, phaseMsg]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [timer.timeLeft, timer.isActive, currentPhase, sessionDuration, timerPhases]);

    useEffect(() => {
        const controller = new AbortController();
        apiService.getMySubmissions({ signal: controller.signal }).then((subs) => {
            if (controller.signal.aborted) return;
            if (Array.isArray(subs)) {
                const map = {};
                subs.forEach(s => {
                    if (!map[s.case_id] || new Date(s.created_at) > new Date(map[s.case_id].created_at)) {
                        map[s.case_id] = s;
                    }
                });
                setCompletedSubmissions(map);
            }
        }).catch(() => { /* Completion indicators are optional. */ });
        return () => controller.abort();
    }, [user?.id]);

    useEffect(() => {
        const controller = new AbortController();
        setCasesLoading(true);
        setCasesError(false);
        apiService.getCases(language, { signal: controller.signal }).then((result) => {
            if (controller.signal.aborted) return;
            if (!result.success || !Array.isArray(result.data)) throw new Error('Invalid case list');
            const available = result.data.map((caseItem) => ({
                ...caseItem,
                chiefComplaint: caseItem.chief_complaint ?? caseItem.chiefComplaint ?? ''
            }));
            setCases(available);
            setSelectedCase((current) => current ? localizeCaseSnapshot(encounterRef.current.caseSnapshot, language) || available.find((item) => item.id === current.id) || current : null);
            setTempSelectedCase((current) => current ? available.find((item) => item.id === current.id) || current : null);
            setMessages((current) => current.map((message) => {
                const snapshot = localizeCaseSnapshot(encounterRef.current.caseSnapshot, language);
                const patient = message.type === 'chart' && (snapshot?.id === message.chart?.caseId ? snapshot : available.find((item) => item.id === message.chart?.caseId));
                return patient ? { ...message, chart: buildInitialChart(patient, message.chart.title) } : message;
            }));
        }).catch((error) => {
            if (error.name !== 'AbortError' && !controller.signal.aborted) setCasesError(true);
        }).finally(() => {
            if (!controller.signal.aborted) setCasesLoading(false);
        });
        return () => controller.abort();
    }, [language, caseRefresh]);

    useEffect(() => {
        const snapshot = localizeCaseSnapshot(encounterRef.current.caseSnapshot, language);
        if (!sessionId || !snapshot) return;
        setSelectedCase((current) => current?.id === snapshot.id ? snapshot : current);
        setMessages((current) => current.map((message) => message.type === 'chart' && message.chart?.caseId === snapshot.id
            ? { ...message, chart: buildInitialChart(snapshot, message.chart.title) }
            : message));
    }, [language, sessionId]);

    function handleTimerExpired() {
        if (!encounterRef.current.sessionId) return;
        setIsTimerExpired(true);
        setCurrentPhase(ASSESSMENT_PHASE);

        const expiryMessage = {
            id: ++nextMessageId.current,
            text: `⏰ ${t('timeUpMessage')}`,
            sender: 'system',
            timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        };

        setMessages(prev => [...prev, expiryMessage]);

        enterAssessment();
    }

    function handleNextPhase() {
        if (!timer.isActive || timer.isExpired || isAssessmentPhase) return;

        const elapsedTime = sessionDuration - timer.timeLeft;
        const currentPhaseIndex = timerPhases.findIndex(p => elapsedTime < p.cumulative);

        // If we're already in the last phase, don't allow skipping
        if (currentPhaseIndex === -1 || currentPhaseIndex >= timerPhases.length - 1) {
            return;
        }

        // Calculate time to skip to reach the next phase
        const nextPhaseCumulative = timerPhases[currentPhaseIndex].cumulative;
        const timeToSkip = nextPhaseCumulative - elapsedTime;

        // Skip the remaining time of current phase
        timer.skipTime(timeToSkip);

        // Add notification message
        const nextPhase = timerPhases[currentPhaseIndex + 1];
        if (nextPhase.name === ASSESSMENT_PHASE) {
            setCurrentPhase(ASSESSMENT_PHASE);
            enterAssessment();
            return;
        }
        const skipMessage = {
            id: ++nextMessageId.current,
            text: `⏭️ ${formatPhaseNotice('phaseSkippedMoving', nextPhase)}`,
            sender: 'system',
            timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        };
        setMessages(prev => [...prev, skipMessage]);
    }

    function handleInactivityTimeout() {
        if (selectedCase && messages.length > 0 && timer.isActive && !timer.isExpired && !isAssessmentPhase && !showEvaluationForm) {
            const randomMessage = inactivityMessages[Math.floor(Math.random() * inactivityMessages.length)];

            const aiResponse = {
                id: ++nextMessageId.current,
                text: randomMessage,
                sender: 'ai',
                timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
            };

            setMessages(prev => [...prev, aiResponse]);
            timer.resetInactivity();
        }
    }

    useEffect(() => {
        if (timer.shouldWarn) {
            handleInactivityTimeout();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [timer.shouldWarn]);

    const handleSelectCase = async (caseItem) => {
        if (sessionActive) {
            alert("Current session is in progress. Please finish the current case or cancel the session to switch cases.");
            return;
        }

        if (isTimerExpired) {
            alert("Session expired! Please complete your evaluation before starting a new case.");
            return;
        }

        if (completedSubmissions[caseItem.id]) {
            setCompletedModalCase(caseItem);
            setShowCompletedModal(true);
            return;
        }

        setTempSelectedCase(caseItem);
        setEncounterError('');
        setPatientImage('');
        setShowTimerModal(true);
    };

    const handleRedoCase = () => {
        setShowCompletedModal(false);
        setTempSelectedCase(completedModalCase);
        setEncounterError('');
        setPatientImage('');
        setShowTimerModal(true);
    };

    const handleViewTranscript = () => {
        const saved = completedSubmissions[completedModalCase.id];
        const backup = loadTranscriptBackups(user?.id)[completedModalCase.id];
        const matchingBackup = backup && (!saved?.id || String(saved.id) === String(backup.id)) ? backup : null;
        const sub = {
            ...(matchingBackup || {}),
            ...(saved || {}),
            case_id: completedModalCase.id,
            transcript: Array.isArray(saved?.transcript) ? saved.transcript : matchingBackup?.transcript || []
        };
        if (sub.id && sub.system_result) {
            setShowCompletedModal(false);
            navigate(`/results/${sub.id}`, {
                state: {
                    submission: {
                        ...sub,
                        patient_name: sub.patient_name || completedModalCase.name
                    }
                }
            });
            return;
        }
        setViewingTranscript(sub);
        setShowCompletedModal(false);
    };

    const loadCaseData = (caseItem) => {
        setMessages([]);
        setEvaluationResult(null);
        setDiagnosis('');
        setManagementPlan('');
        setShowEvaluationForm(false);
        setShowImageModal(false);
        setPatientImage('');
        setShowLabReports(false);
        setShowAnketaPanel(false);

        const initialMessage = {
            id: ++nextMessageId.current,
            sender: 'system',
            type: 'chart',
            chart: buildInitialChart(caseItem, t('medicalCaseAnketa')),
            timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        };
        setMessages([initialMessage]);

        const imageUrl = getPatientImageUrl(caseItem.id);
        const encounter = encounterRef.current;
        const testImage = new Image();
        testImage.onload = () => {
            if (encounterRef.current === encounter) setPatientImage(imageUrl);
        };
        testImage.onerror = () => {
            if (encounterRef.current === encounter) setPatientImage('');
        };
        testImage.src = imageUrl;
    };

    const handleStartSession = async (configuredPhases = DEFAULT_PHASES) => {
        if (!tempSelectedCase || encounterRef.current.pending || encounterRef.current.sessionId) return;
        if (!configuredPhases.length || configuredPhases.some((phase) => !Number.isFinite(Number(phase.minutes)) || Number(phase.minutes) < 1)) return;
        invalidateEncounter();
        const encounter = { controller: new AbortController(), sessionId: null, pending: 'start' };
        encounterRef.current = encounter;
        setIsStarting(true);
        setEncounterError('');
        try {
            const result = await apiService.startSession(tempSelectedCase.id, { signal: encounter.controller.signal, language });
            if (encounterRef.current !== encounter || encounter.controller.signal.aborted) return;
            if (!result.success || !result.data?.session_id) throw new Error('Invalid session response');
            if (result.data.case && result.data.case.id !== tempSelectedCase.id) throw new Error('Invalid session case');
            encounter.sessionId = result.data.session_id;
            encounter.caseSnapshot = result.data.case || null;
            const sessionCase = localizeCaseSnapshot(encounter.caseSnapshot, language) || tempSelectedCase;
            const nextPhases = compilePhases(configuredPhases);
            const nextDuration = getTotalDuration(nextPhases);
            setTimerPhases(nextPhases);
            setSessionDuration(nextDuration);
            setCurrentPhase(nextPhases[0].name);
            setPhaseTimeRemaining(nextPhases[0].duration);
            setSelectedCase(sessionCase);
            setSessionId(encounter.sessionId);
            loadCaseData(sessionCase);
            timer.start(nextDuration);
            setSessionActive(true);
            setShowTimerModal(false);
            setTempSelectedCase(null);
        } catch (error) {
            if (error.name !== 'AbortError' && encounterRef.current === encounter) setEncounterError(copy.startError);
        } finally {
            if (encounterRef.current === encounter) {
                encounter.pending = null;
                setIsStarting(false);
            }
        }
    };

    const handleSend = async (e) => {
        e.preventDefault();

        const encounter = encounterRef.current;
        if (!inputText.trim() || !selectedCase || !encounter.sessionId || encounter.pending || !sessionActive) return;

        if (isTimerExpired || isAssessmentPhase) {
            alert(t('mustCompleteEval'));
            enterAssessment();
            return;
        }

        const userMessage = {
            id: ++nextMessageId.current,
            text: inputText.trim(),
            sender: 'user',
            timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        };

        setMessages(prev => [...prev, userMessage]);
        setInputText('');
        setIsTyping(true);
        setEncounterError('');
        encounter.pending = 'chat';
        const showUnsentNotice = (text) => {
            const notice = { id: ++nextMessageId.current, text, sender: 'system', timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) };
            setMessages((current) => [
                ...current.filter((message) => message.id !== userMessage.id),
                notice
            ]);
            // Preserve a new draft typed while waiting for the response.
            setInputText((current) => current || userMessage.text);
        };

        try {
            timer.resetInactivity();
            const transcript = buildTranscript(messages);
            const result = await apiService.sendChatMessage(
                selectedCase.id,
                userMessage.text,
                currentPhase,
                transcript,
                language,
                { sessionId: encounter.sessionId, signal: encounter.controller.signal }
            );
            if (encounterRef.current !== encounter || encounter.controller.signal.aborted) return;
            if (result.success && typeof result.data?.response === 'string') {
                if (result.data.limit_reached) {
                    showUnsentNotice(copy.chatLimit);
                    return;
                }
                const aiResponse = {
                    id: ++nextMessageId.current,
                    text: result.data.response,
                    sender: result.data.responder === 'system' ? 'system' : 'ai',
                    transcriptRole: result.data.responder === 'system' ? 'system' : 'patient',
                    timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                };
                setMessages((current) => [
                    ...current.map((message) => message.id === userMessage.id ? { ...message, transcriptRole: 'user' } : message),
                    aiResponse
                ]);
            } else throw new Error('Invalid chat response');
        } catch (error) {
            if (error.name === 'AbortError' || encounterRef.current !== encounter) return;

            let errorMessage = t('processingError');

            if (error.message === 'Authentication expired') {
                errorMessage = t('authExpired');
            }

            showUnsentNotice(`⚠️ ${errorMessage}`);
        } finally {
            if (encounterRef.current === encounter) {
                encounter.pending = null;
                setIsTyping(false);
            }
        }
    };

    const handleSubmitEvaluation = async () => {
        const encounter = encounterRef.current;
        if (!encounter.sessionId || encounter.pending || !sessionActive) {
            if (encounter.pending === 'chat') alert(copy.waitForResponse);
            return;
        }
        if (!diagnosis.trim() || !managementPlan.trim() || !selectedCase) {
            alert(t('fillBoth'));
            return;
        }

        setIsEvaluating(true);
        encounter.pending = 'evaluate';
        try {
            const transcript = buildTranscript(messages);

            const result = await apiService.evaluateDiagnosis(
                selectedCase.id,
                diagnosis,
                managementPlan,
                transcript,
                language,
                { sessionId: encounter.sessionId, signal: encounter.controller.signal }
            );
            if (encounterRef.current !== encounter || encounter.controller.signal.aborted) return;
            if (result.success && result.data?.submission_id) {
                const savedSubmission = {
                    id: result.data?.submission_id,
                    case_id: selectedCase.id,
                    patient_name: selectedCase.name,
                    created_at: new Date().toISOString(),
                    system_score: result.data?.total_score ?? result.data?.score ?? null,
                    system_result: result.data,
                    language: result.data?.language || language,
                    transcript: Array.isArray(result.data?.transcript) ? result.data.transcript : transcript,
                    student_diagnosis: typeof result.data.student_diagnosis === 'string' ? result.data.student_diagnosis : diagnosis,
                    student_plan: typeof result.data.student_plan === 'string' ? result.data.student_plan : managementPlan
                };
                saveTranscriptBackup(user?.id, selectedCase.id, savedSubmission);
                setCompletedSubmissions(prev => ({
                    ...prev,
                    [selectedCase.id]: savedSubmission
                }));
                setIsTimerExpired(false);
                setSessionActive(false);
                setSessionId(null);
                timer.reset();
                setShowEvaluationForm(false);
                encounter.sessionId = null;
                navigate(`/results/${result.data.submission_id}`, {
                    state: { submission: savedSubmission }
                });
            } else throw new Error('Invalid evaluation response');
        } catch (error) {
            if (error.name === 'AbortError' || encounterRef.current !== encounter) return;
            if (error.message === 'Authentication expired') {
                alert(t('authExpiredRelogin'));
            } else {
                alert(t('evaluationSubmitError'));
            }
        } finally {
            if (encounterRef.current === encounter) {
                encounter.pending = null;
                setIsEvaluating(false);
            }
        }
    };

    const handleClearChat = () => {
        invalidateEncounter();
        setSelectedCase(null);
        setTempSelectedCase(null);
        setMessages([]);
        setInputText('');
        setIsTyping(false);
        setIsEvaluating(false);
        setIsStarting(false);
        setEncounterError('');
        setEvaluationResult(null);
        setDiagnosis('');
        setManagementPlan('');
        setShowEvaluationForm(false);
        setShowImageModal(false);
        setShowTimerModal(false);
        setPatientImage('');
        setImageZoom(1);
        setIsTimerExpired(false);
        setSessionActive(false);
        setSessionId(null);
        setShowLabReports(false);
        setShowAnketaPanel(false);
        timer.reset();
    };

    const handleReturnToDashboard = () => {
        if (sessionActive && selectedCase) {
            const confirmLeave = window.confirm(t('returnDashboardConfirm'));
            if (!confirmLeave) return;
        }
        handleClearChat();
    };

    const handleQuickQuestion = (question) => {
        if (isTimerExpired || isAssessmentPhase) {
            alert(t('mustCompleteEval'));
            enterAssessment();
            return;
        }
        setInputText(question);
    };

    const resetImageZoom = () => {
        setImageZoom(1);
    };

    const zoomIn = () => {
        setImageZoom(prev => Math.min(prev + 0.2, 3));
    };

    const zoomOut = () => {
        setImageZoom(prev => Math.max(prev - 0.2, 0.5));
    };

    const handleEvaluateClick = () => {
        if (encounterRef.current.pending) {
            if (encounterRef.current.pending === 'chat') setEncounterError(copy.waitForResponse);
            return;
        }
        if (isTimerExpired) {
            setShowEvaluationForm(true);
        } else if (timer.timeLeft > 0) {
            const confirm = window.confirm(t('goEvalConfirm'));
            if (confirm) {
                setShowEvaluationForm(true);
            }
        } else {
            setShowEvaluationForm(true);
        }
    };

    const getCaseImageUrl = getPatientImageUrl;

    const openSubscriptionPage = () => {
        navigate('/subscription');
    };

    return (
        <div className={`app ${!selectedCase && !sessionActive ? 'case-library-mode' : 'encounter-workspace'}`}>
            {(selectedCase || sessionActive) && (
                <Sidebar
                    cases={cases}
                    selectedCase={selectedCase}
                    onSelectCase={handleSelectCase}
                    onClose={() => setSidebarOpen(false)}
                    onClearChat={handleClearChat}
                    isOpen={sidebarOpen}
                    completedSubmissions={completedSubmissions}
                />
            )}

            <div className="main-content">
                <TopNav
                    onMenuClick={() => setSidebarOpen(true)}
                    onClearChat={handleClearChat}
                    onEvaluateClick={handleEvaluateClick}
                    onToggleAnketa={() => setShowAnketaPanel(true)}
                    onNextPhase={handleNextPhase}
                    selectedCase={selectedCase}
                    sessionActive={sessionActive}
                    sessionId={sessionId}
                    currentPhase={currentPhase}
                    currentPhaseIndex={timerPhases.findIndex(p => (sessionDuration - timer.timeLeft) < p.cumulative)}
                    totalPhases={timerPhases.length}
                    timer={timer}
                    phaseTimeRemaining={phaseTimeRemaining}
                    showLabReports={showLabReports}
                    onToggleLabReports={() => setShowLabReports(!showLabReports)}
                    onReturnToDashboard={handleReturnToDashboard}
                    onSubscriptionClick={openSubscriptionPage}
                    showMenuButton={Boolean(selectedCase || sessionActive)}
                />

                {encounterError && !showTimerModal && <p role="alert" className="error-state">{encounterError}</p>}

                {selectedCase ? (
                    <>
                        <PatientHeader
                            selectedCase={selectedCase}
                            patientImage={patientImage}
                            onImageClick={() => setShowImageModal(true)}
                            timer={timer}
                            sessionActive={sessionActive}
                            currentPhase={currentPhase}
                            phaseTimeRemaining={phaseTimeRemaining}
                            formatPhaseTime={(ms) => {
                                const totalSeconds = Math.floor(ms / 1000);
                                const minutes = Math.floor(totalSeconds / 60);
                                const seconds = totalSeconds % 60;
                                return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
                            }}
                            currentPhaseIndex={timerPhases.findIndex(p => (sessionDuration - timer.timeLeft) < p.cumulative)}
                            totalPhases={timerPhases.length}
                            onNextPhase={handleNextPhase}
                        />

                        <ChatContainer
                            messages={messages}
                            selectedCase={selectedCase}
                            isTyping={isTyping}
                        />

                        {!showEvaluationForm && !isTimerExpired && !isAssessmentPhase && sessionActive && (
                            <ChatInput
                                inputText={inputText}
                                onInputChange={setInputText}
                                onSend={handleSend}
                                onQuickQuestion={handleQuickQuestion}
                                currentPhase={currentPhase}
                                disabled={isTyping || isEvaluating}
                            />
                        )}
                    </>
                ) : (
                    <div className="case-selection">
                        {sessionActive ? (
                            <div className="error-state">
                                <h3>⚠️ {t('sessionActiveNoCase')}</h3>
                                <p>{t('syncProblemDetail')}</p>
                                <p>{t('sessionId')}: {sessionId || t('pendingShort')}</p>
                                <button
                                    className="btn-primary"
                                    onClick={() => {
                                        setSessionActive(false);
                                        setSessionId(null);
                                        timer.reset();
                                        window.location.reload();
                                    }}
                                >
                                    {t('resetDashboard')}
                                </button>
                            </div>
                        ) : (
                            <>
                                <section className="case-selection-hero">
                                    <div>
                                        <span className="case-kicker">{t('clinicalLibrary')}</span>
                                        <h2>
                                            <span className="headline-tool">🩺</span>
                                            {t('selectEncounter')}
                                        </h2>
                                        <p>{t('libraryIntro')}</p>
                                    </div>
                                </section>

                                {casesLoading && cases.length === 0 && (
                                    <section className="case-loading-grid" aria-label="Loading patient cases">
                                        {[1, 2, 3].map(item => (
                                            <div className="case-loading-card" key={item}>
                                                <span />
                                                <strong />
                                                <p />
                                            </div>
                                        ))}
                                    </section>
                                )}

                                {casesError && <div role="alert" className="error-state"><p>{copy.casesError}</p><button type="button" onClick={() => setCaseRefresh((value) => value + 1)}>{copy.retry}</button></div>}
                                <section className="case-gallery" aria-label="Available patient cases">
                                    {cases.map((caseItem) => {
                                        const completed = completedSubmissions[caseItem.id];
                                        return (
                                            <button
                                                type="button"
                                                className="case-tile"
                                                key={caseItem.id}
                                                onClick={() => handleSelectCase(caseItem)}
                                            >
                                                <div className="case-tile-image">
                                                    <img
                                                        src={getCaseImageUrl(caseItem.id)}
                                                        alt={caseItem.name}
                                                        onError={(event) => {
                                                            event.currentTarget.classList.add('is-hidden');
                                                        }}
                                                    />
                                                    <span className="case-tile-fallback">🩺</span>
                                                    <span className={completed ? 'case-tile-status complete' : 'case-tile-status'}>
                                                        {completed ? t('completed') : t('startCase')}
                                                    </span>
                                                </div>
                                                <div className="case-tile-body">
                                                    <div className="case-tile-meta">
                                                        <span>{caseItem.age} {t('years')}</span>
                                                        <span>{completed ? t('reviewAvailable') : t('timedEncounter')}</span>
                                                    </div>
                                                    <h3>{caseItem.name}</h3>
                                                    <p>{caseItem.chiefComplaint}</p>
                                                </div>
                                            </button>
                                        );
                                    })}
                                </section>
                            </>
                        )}
                    </div>
                )}

                {isTimerExpired && !showEvaluationForm && (
                    <div className="session-expired">
                        <div className="expired-message">
                            <h3>⏰ {t('sessionExpired')}</h3>
                            <p>{t('yourExamTimeEnded')}</p>
                            <button
                                className="btn-primary"
                                onClick={() => setShowEvaluationForm(true)}
                            >
                                {t('goToEvaluation')}
                            </button>
                        </div>
                    </div>
                )}
            </div>

            <EvaluationModal
                isOpen={showEvaluationForm}
                onClose={() => {
                    if (isEvaluating) return;
                    if ((isTimerExpired || isAssessmentPhase) && !evaluationResult) return;
                    setShowEvaluationForm(false);
                    if (evaluationResult) {
                        setEvaluationResult(null);
                    }
                }}
                isEvaluating={isEvaluating}
                canClose={!isTimerExpired && !isAssessmentPhase}
                assessmentPhase={isAssessmentPhase}
                timeRemaining={sessionActive ? timer.formattedTime : undefined}
                isTimeExpired={isTimerExpired}
                isChatPending={isTyping}
                acceptedTranscript={acceptedTranscript}
                diagnosis={diagnosis}
                managementPlan={managementPlan}
                evaluationResult={evaluationResult}
                onDiagnosisChange={setDiagnosis}
                onManagementPlanChange={setManagementPlan}
                onSubmitEvaluation={handleSubmitEvaluation}
                onLeaveEncounter={handleReturnToDashboard}
            />

            <ImageModal
                isOpen={showImageModal}
                onClose={() => setShowImageModal(false)}
                patientImage={patientImage}
                selectedCase={selectedCase}
                imageZoom={imageZoom}
                onZoomIn={zoomIn}
                onZoomOut={zoomOut}
                onResetZoom={resetImageZoom}
            />

            <TimerModal
                isOpen={showTimerModal}
                onClose={() => {
                    if (isStarting) invalidateEncounter();
                    setIsStarting(false);
                    setEncounterError('');
                    setShowTimerModal(false);
                    setTempSelectedCase(null);
                }}
                onStartSession={handleStartSession}
                isStarting={isStarting}
                error={encounterError}
                startingLabel={copy.starting}
                patient={tempSelectedCase}
                patientImage={tempSelectedCase ? getCaseImageUrl(tempSelectedCase.id) : ''}
                phases={timerPhases.map(({ name, duration }) => ({
                    name,
                    minutes: Math.round(duration / 60000)
                }))}
                defaultPhases={DEFAULT_PHASES}
            />

            <LabReportsPanel
                isOpen={showLabReports}
                onClose={() => setShowLabReports(false)}
                caseData={selectedCase}
                sessionId={sessionId}
                onReportsRequested={(reports) => setMessages(current => {
                    const additions = reports.filter(report => report.investigation_id && ['user', 'system'].includes(report.role)).map(report => ({
                        id: `report-${sessionId}-${report.investigation_id}-${report.role}`,
                        investigationId: report.investigation_id,
                        sender: report.role === 'user' ? 'user' : 'system',
                        text: report.content,
                        transcriptRole: report.role,
                        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                    }));
                    return [...current, ...additions.filter(message => !current.some(existing => existing.id === message.id))];
                })}
            />

            <AnketaPanel
                isOpen={showAnketaPanel}
                onClose={() => setShowAnketaPanel(false)}
                caseData={selectedCase}
            />

            {sidebarOpen && (
                <div className="overlay" onClick={() => setSidebarOpen(false)}></div>
            )}

            {/* Completed case modal */}
            {showCompletedModal && completedModalCase && (
                <div className="modal-overlay" onClick={() => setShowCompletedModal(false)}>
                    <div className="completed-case-modal" onClick={e => e.stopPropagation()}>
                        <h3>{t('completedCase')}</h3>
                        <p className="completed-case-name">{completedModalCase.name}</p>
                        <p className="completed-case-subtitle">{t('whatNext')}</p>
                        <div className="completed-case-actions">
                            <button className="btn-outline" onClick={handleViewTranscript}>
                                {t('viewPreviousTranscript')}
                            </button>
                            <button className="btn-primary" onClick={handleRedoCase}>
                                {t('startFresh')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Transcript viewer modal */}
            {viewingTranscript && (
                <div className="modal-overlay" onClick={() => setViewingTranscript(null)}>
                    <div className="transcript-modal" onClick={e => e.stopPropagation()}>
                        <div className="transcript-modal-header">
                            <h3>{t('yourPreviousTranscript')}</h3>
                            <button className="modal-close-btn" onClick={() => setViewingTranscript(null)}>✕</button>
                        </div>
                        {viewingTranscript.system_score != null && (
                            <div className="transcript-score-row">
                                <span>{t('aiScore')}: <strong>{viewingTranscript.system_score}/100</strong></span>
                                <span style={{ color: '#64748b', fontSize: '0.85rem' }}>
                                    {new Date(viewingTranscript.created_at).toLocaleString()}
                                </span>
                            </div>
                        )}
                        <div className="transcript-messages">
                            {(viewingTranscript.transcript || []).length === 0
                                ? <p className="transcript-empty">{t('noTranscriptRecorded')}</p>
                                : (viewingTranscript.transcript || []).map((msg, i) => (
                                    <div key={i} className={`transcript-msg transcript-${msg.role}`}>
                                        <span className="transcript-role">
                                            {msg.role === 'user' ? t('youStudent') : t('patient')}
                                        </span>
                                        <p>{msg.content}</p>
                                    </div>
                                ))
                            }
                        </div>
                        {viewingTranscript.student_diagnosis && (
                            <div className="transcript-written">
                                <h4>{t('yourDiagnosis')}</h4>
                                <p>{viewingTranscript.student_diagnosis}</p>
                            </div>
                        )}
                        {viewingTranscript.student_plan && (
                            <div className="transcript-written">
                                <h4>{t('yourManagementPlan')}</h4>
                                <p>{viewingTranscript.student_plan}</p>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

export default Dashboard;
